import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { forkJoin, lastValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileResponse, IFile } from '../interfaces/file.interface';
import { AuthService } from './auth.service';
import { Directory, PresignedDirectoryRequest } from '../interfaces/directory.interface';
import { TypesEnum } from '../enums/shared-type.enum';

type PreSignedUploadDto = {
    webKitRelativePath: string;
    directory: {
        id: string;
        path: string;
    };
    fileMetadata: {
        name: string;
        extension: string;
        size: number;
        contentType: string;
    };
};

@Injectable({ providedIn: 'root' })
export class FileService {
    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private readonly http: HttpClient,
        private handler: HttpBackend,
        private readonly authService: AuthService
    ) {}

    getFilesByDirectory(
        directoryId: string,
        recycleBin: boolean = false,
        params?: {
            page?: number;
            limit?: number;
            nodeType?: string;
        }
    ): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let httpParams = new HttpParams();

        httpParams = httpParams.set('inRecycleBin', recycleBin.toString()).set('nodeType', params?.nodeType || 'FILE');

        if (directoryId) {
            httpParams = httpParams.set('directoryId', directoryId);
        }

        if (params?.page) {
            httpParams = httpParams.set('page', params.page.toString());
        }

        if (params?.limit) {
            httpParams = httpParams.set('limit', params.limit.toString());
        }

        return this.http
            .get<FileResponse>(`${environment.apiUrls.coreApi}/nodes/types`, {
                params: httpParams,
                headers
            })
            .pipe(
                map((response) => ({
                    ...response,
                    content: response.content.map((file) => this.mapFileData(file))
                }))
            );
    }

    preAuthorizeUpload(path: string, files: File[], directory: Directory) {
        return this.getPresignedUrls(files, {
            id: directory.id,
            path: path
        });
    }

    async executeUpload(uploadData: any, file: File, directory: PresignedDirectoryRequest, onProgress: (percent: number) => void) {
        try {
            // Passamos o objeto 'uploadData' completo para o uploadToS3
            await this.uploadToS3(file, uploadData, file.type || 'application/octet-stream', directory, onProgress);
            // console.log(`✅ Upload concluído para ${file.name}`);
            onProgress(100);
        } catch (error) {
            console.error(`❌ Falha no upload para ${file.name}:`, error);
            throw error; // Importante relançar para o componente capturar no catch
        }
    }

    viewFile(key: string, path: string): Observable<{ base64: string; contentType: string }> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        return this.http.post<{
            base64: string;
            contentType: string;
        }>(`${environment.apiUrls.uploadApi}/download/base64`, { fileKey: key, path }, { headers });
    }

    generateOnlyOfficeUrl(data: { fileKey: string; path?: string }): Observable<{ url: string; editorConfig: any }> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post<{ url: string; editorConfig: any }>(`${environment.apiUrls.uploadApi}/onlyoffice/edit-url`, data, { headers });
    }

    renameFile(fileId: string, newName: string): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body = {
            name: newName,
            nodeType: TypesEnum.DIRECTORY
        };
        return this.http.patch<any>(`${environment.apiUrls.coreApi}/nodes/${fileId}/rename`, body, { headers });
    }

    recycleOrRestoreItem(fileId: string): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.patch(`${environment.apiUrls.coreApi}/nodes/${fileId}/recycle-bin`, { headers });
    }

    deletePermanently(rootId: string): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.delete(`${environment.apiUrls.coreApi}/nodes/${rootId}`, { headers });
    }

    deletePermanently_deprecated(fileKeys: string[], directoryIds: string[]): Observable<any> {
        if (fileKeys.length > 0) {
            // 1. Observable para deletar arquivos físicos (UploadAPI - Batch)
            const deleteFiles$ = this.deleteFileFromS3(fileKeys);
            const deleteDirectoryRequests = this.deleteDirectory(directoryIds);
            return deleteFiles$.pipe(
                switchMap(() => {
                    if (directoryIds.length > 0) {
                        // forkJoin executa todas as requisições de DELETE em paralelo
                        // e só "completa" quando a última responder.
                        return forkJoin(deleteDirectoryRequests);
                    }
                    return of({ message: 'Apenas arquivos foram excluídos.' });
                })
            );
        }

        // 2. Função para criar as requests de diretórios (CoreAPI - Unitário)
        return forkJoin(this.deleteDirectory(directoryIds));
    }

    private deleteDirectory(directoryIds: string[]) {
        return directoryIds.map((id) => this.http.delete(`${environment.apiUrls.coreApi}/nodes/${id}`));
    }

    private deleteFileFromS3(fileKeys: string[]): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.delete(`${environment.apiUrls.uploadApi}/delete`, {
            body: { keys: fileKeys },
            headers
        });
    }

    private mapFileData(file: any): IFile {
        const fileName = file.originalName || file.name || 'Arquivo sem nome.raw';
        return {
            ...file,
            createdAt: this.formatDateBrl(file.createdAt),
            name: fileName,
            size: Math.round((file.contentLength || 0) / 1024),
            type: this.getFileExtension(fileName),
            lastModified: this.formatDateBrl(file.updatedAt)
        };
    }

    formatDateBrl(dateInput: any) {
        const rawDate = dateInput;
        return Array.isArray(rawDate) ? new Date(rawDate[0], rawDate[1] - 1, rawDate[2], rawDate[3], rawDate[4], rawDate[5]) : new Date(rawDate);
    }

    private getFileExtension(filename: string): string {
        if (!filename) return 'unknown';
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop()?.toLowerCase() || 'unknown' : 'unknown';
    }

    // 1️⃣ Gera URLs pré-assinadas para os arquivos
    private getPresignedUrls(files: File[], directory: PresignedDirectoryRequest) {
        const body: PreSignedUploadDto[] = files.map((file) => ({
            directory: {
                id: directory.id,
                path: directory.path
            },
            fileMetadata: {
                contentType: file.type || 'application/octet-stream',
                extension: file.name.match(/\.([^.]+)$/)?.[1] || '',
                name: file.name,
                size: file.size
            },
            webKitRelativePath: file.webkitRelativePath
        })) as any[];

        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.authService.getToken()}`,
            'Content-Type': 'application/json'
        });

        return this.http.post<{ uploadUrls: any[] }>(`${environment.apiUrls.uploadApi}/aws/upload-file/presigned`, body, { headers });
    }

    private async uploadToS3(
        file: any,
        presignedData: any, // Alterado para receber o objeto que vem do seu Back (com url ou urls[])
        contentType: string,
        directory: PresignedDirectoryRequest,
        onProgress: (percent: number) => void
    ): Promise<any> {
        // 1. Identifica se é Multipart ou Simples
        const isMultipart = !!presignedData.isMultipart;

        // Se não for Multipart, extraímos a URL simples para manter o fluxo original
        const url = isMultipart ? null : presignedData.url || presignedData;

        let currentUser: any = this.authService.getCurrentUserInfo();
        if (currentUser) currentUser = JSON.parse(currentUser);

        const cleanedPath = file.webkitRelativePath ? directory.path.concat('/').concat(file.webkitRelativePath.substring(0, file.webkitRelativePath.lastIndexOf('/'))) : directory.path;
        // Headers originais (Serão usados apenas no PUT simples)
        const rawHeaders: any = {
            'Content-Type': contentType || 'application/octet-stream',
            'x-amz-meta-directory_path': this.cleanPath(this.normalizeString(cleanedPath)),
            'x-amz-meta-directory_path_id': String(directory.id),
            'x-amz-meta-original_file_name': this.sanitizeString(file.name),
            'x-amz-meta-user_id': String(currentUser?.user.id),
            'x-amz-meta-tenant_id': String(currentUser?.user.tenantId)
        };

        if (!isMultipart) {
            // --- FLUXO ORIGINAL (RETROCOMPATIBILIDADE) ---
            return this.executeFetch(url, rawHeaders, file);
        } else {
            // --- NOVO FLUXO MULTIPART ---
            const { uploadId, urls, key } = presignedData;
            const chunkSize = 5 * 1024 * 1024; // 5MB definido no seu Back
            const completedParts: any[] = [];

            for (let i = 0; i < urls.length; i++) {
                const start = i * chunkSize;
                const end = Math.min(start + chunkSize, file.size);
                const blobPart = file.slice(start, end);

                // IMPORTANTE: Para as partes, enviamos apenas o Content-Type (ou headers assinados)
                // O S3 rejeita os x-amz-meta se eles não foram incluídos na assinatura da parte
                const partHeaders = { 'Content-Type': rawHeaders['Content-Type'] };

                const res = await this.executeFetch(urls[i], partHeaders, blobPart);

                // ATUALIZA O PROGRESSO BASEADO NAS PARTES
                const percent = Math.round(((i + 1) / urls.length) * 100);
                onProgress(percent);

                const etag = res.headers.get('ETag');
                completedParts.push({
                    partNumber: i + 1,
                    eTag: etag?.replace(/"/g, '')
                });
            }

            // Após todas as partes, chama o Back-end para finalizar
            // Você precisará implementar esse finalizeMultipart no seu Service
            return this.finalizeMultipart(uploadId, key, completedParts);
        }
    }

    /** * Helper para isolar o fetch e manter a lógica de erro centralizada
     */
    private async executeFetch(url: string, headers: any, body: any): Promise<Response> {
        const res = await fetch(url, {
            method: 'PUT',
            headers: new Headers(headers),
            body: body
        });

        if (!res.ok) {
            const errorBody = await res.text().catch(() => 'N/A');
            throw new Error(`[S3 Upload Error] Status: ${res.status}. Body: ${errorBody}`);
        }
        return res;
    }

    private async finalizeMultipart(uploadId: string, key: string, parts: { partNumber: number; eTag: string }[]): Promise<any> {
        const url = `${environment.apiUrls.uploadApi}/aws/upload-file/complete`; // Ajuste para o seu endpoint

        const body = {
            uploadId: uploadId,
            key: key,
            parts: parts
        };

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.authService.getToken()}` // Se necessário
                },
                body: JSON.stringify(body)
            });

            if (!res.ok) {
                throw new Error(`Erro ao finalizar multipart upload: ${res.statusText}`);
            }

            return await res.json();
        } catch (error) {
            console.error('❌ Falha na finalização do Multipart no Backend', error);
            throw error;
        }
    }

    private handleUploadError(error: any): never {
        if (error instanceof HttpErrorResponse) {
            let errorText = error.error;

            // Caso o erro venha como Blob (comum em uploads), precisamos converter para texto
            if (errorText instanceof Blob) {
                console.warn('⚠️ O corpo do erro é um Blob. Verifique o log do Network para o XML completo.');
            }

            // Regex aprimorada para capturar detalhes extras do S3
            const errorCode = errorText?.toString().match(/<Code>(.*?)<\/Code>/)?.[1] || 'UnknownError';
            const errorMessage = errorText?.toString().match(/<Message>(.*?)<\/Message>/)?.[1] || 'Erro de comunicação com S3';
            const extraDetail = errorText?.toString().match(/<ArgumentName>(.*?)<\/ArgumentName>/)?.[1] || errorText?.toString().match(/<HeaderName>(.*?)<\/HeaderName>/)?.[1] || '';

            console.error(`❌ S3 Error [${error.status}]: ${errorCode} - ${errorMessage} ${extraDetail ? `(Campo: ${extraDetail})` : ''}`);

            if (error.status === 0) {
                throw new Error('❌ Erro de Rede ou CORS: Verifique se o bucket S3 tem o CORS configurado para o método PUT e os Headers enviados.');
            }

            switch (error.status) {
                case 400:
                    // O erro 400 geralmente é Bad Request por headers incompatíveis ou malformados
                    throw new Error(`[400] Requisição Inválida: O S3 rejeitou os parâmetros. ${extraDetail ? `Verifique o header: ${extraDetail}` : errorMessage}`);
                case 403:
                    throw new Error(`[403] Assinatura/Permissão: A URL expirou ou os headers enviados não batem com o que o Java assinou.`);
                default:
                    throw new Error(`${errorCode}: ${errorMessage}`);
            }
        }

        throw new Error(`❌ Falha inesperada no Upload: ${error.message || error}`);
    }

    sanitizeString(stringToSanitize: string) {
        const withoutAccents = this.normalizeString(stringToSanitize);
        return withoutAccents.replace(/[^a-zA-Z0-9\.\-_ ]/g, '');
    }

    normalizeString(stringToSanitize: string) {
        if (!stringToSanitize) {
            return '';
        }
        const normalized = stringToSanitize.normalize('NFD');
        return normalized.replace(/[\u0300-\u036f]/g, '');
    }

    cleanPath = (path: string): string => {
        if (!path) return '';

        return path
            .replace(/&/g, 'e') // & → e
            .replace(/@/g, 'at') // @ → at
            .replace(/\\/g, '/') // path.replace("\\", "/") -> global no JS
            .replace(/\/+/g, '/') // .replaceAll("/+", "/")
            .replace(/^\/|\/$/g, ''); // .replaceAll("^/|/$", "")
    };
}
